import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../../../../Services/data.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { AccountModel } from '../../../../DataModels/account-model';
import { ConfirmDialogService } from '../../../../Services/confirm-dialog.service';
import { AccountSubtotalModel } from '../../../../DataModels/account-subtotal-model';
import { AccountMarginModel } from '../../../../DataModels/account-margin-model';
import { UomModel } from '../../../../DataModels/uom-model';
import { ResponseModel } from '../../../../DataModels/response-model';
import { take } from 'rxjs/operators';
import { BsToastService } from '../../../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';
import {NavigationService} from '../../../../Services/navigation.service';
import * as xlsx from 'xlsx';
import {Popover} from 'bootstrap';


@Component({
  selector: 'app-account-form',
  templateUrl: './account-form.component.html',
  styleUrls: ['./account-form.component.css'],
  standalone: false
})
export class AccountFormComponent implements OnInit, AfterViewInit {
  @ViewChild('accountPrintRoot') accountPrintRoot: ElementRef;
  reportDataToSendInEmail: any = {};
  account: AccountModel;
  accountForm: UntypedFormGroup;
  accountID;

  subTotal: AccountSubtotalModel[];
  margin: AccountMarginModel[];
  uom: UomModel[];
  submitted = false;
  stopEdit = false;
  field: object;
  hierarchicalAccounts: any;
  items: any;
  searchText: string;

  editSubtotalId: string;
  deleteSubtotalId: string;
  deleteSubtotalName: string;

  editAccountId: any;
  newAccountSubtotalId: any = '';

  auditRecId: string;

  buttonConfig: any = {
    save: false,
    close: true,
    reset: false,
    delete: false,
    saveNew: false,
    saveClose: false,
    export: true,
    print: true,
    email: true,
  };

  constructor(
    private table: DataService,
    private notification: BsToastService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private confirmDialogService: ConfirmDialogService,
    private titleService: Title,
    private nav: NavigationService,
  ) {
    this.createForm();

    this.searchText = this.route.snapshot.params.id;
    this.accountForm.patchValue({ search: this.searchText });
  }

  createForm() {
    this.accountForm = this.formBuilder.group({
      id: new UntypedFormControl(''),
      name: new UntypedFormControl(''),
      number: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$')]),
      subtotal_id: new UntypedFormControl(''),
      margin_id: new UntypedFormControl(''),
      uom_id: new UntypedFormControl(''),
      debit: new UntypedFormControl(true),
      search: new UntypedFormControl(''),
      active: new UntypedFormControl(true),
    });
  }

  ngOnInit() {
    // Initialize model and load hierarchy
    this.account = new AccountModel();
    this.loadAccountHierarchy().then();
  }

  ngAfterViewInit() {
    this.titleService.setTitle($localize`:Accounts @@accounts:Accounts`);

    // Initialize Bootstrap 5 Popovers for any elements marked with data-bs-toggle="popover"
    try {
      const popoverTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="popover"]'));
      popoverTriggerList.forEach((el: Element) => {
        // Use html + container body so long content renders correctly and outside overflow contexts
        new Popover(el, {html: true, trigger: 'hover focus', container: 'body'} as any);
      });
    } catch (e) {
      // no-op: if Bootstrap JS not loaded or no elements present, safely ignore
    }
  }

  getChildListPopover(subId: string) {
    // alert('get children for: ' + subId);
    let matchingSubs = [];
    let subNameList = '';
    matchingSubs = this.hierarchicalAccounts.filter((record) => {
      return record.parent_id === subId;
    });
    subNameList += matchingSubs.map(
      // (element) => '&#x2022; <a>' + element.subtotal_name + '<a></br>'// for ngx toast format
      (element) => ' &#x2022; <a> ' + element.subtotal_name + '<a><br>'
    );
    const finalList = subNameList.replace(/,/g, '');

    // console.log(JSON.stringify(finalList));

    this.notification.showInfoWhiteToast(finalList, $localize`:Child Accounts @@childAccounts:Child Accounts`);
  }

  get f() {
    return this.accountForm.controls;
  }

  private async loadAccountHierarchy() {
    // load the main table
    this.table
      .getData('account/nested?pageNumber=0&pageSize=999999')
      .pipe(take(1))
      .subscribe({
        next: (result: ResponseModel) => {
          this.hierarchicalAccounts = result.Data;
          // console.log('data: ' + JSON.stringify(result.Data));
          this.accountForm.patchValue(this.account);
        },
        error: (err: any) => {
          console.error(err.message);
        },
        complete: () => {
          // do stuff on complete
        },
      });
  }

  loadLookups() {
    this.table
      .getApiListData('account_subtotal?pageNumber=0&pageSize=999999')
      .pipe(take(1))
      .subscribe((res: ResponseModel) => {
        this.subTotal = res.Data;
      });

    this.table
      .getApiListData('account_margin?pageNumber=0&pageSize=999999')
      .pipe(take(1))
      .subscribe((res: ResponseModel) => {
        this.margin = res.Data;
      });

    this.table
      .getApiListData('uom?pageNumber=0&pageSize=999999')
      .pipe(take(1))
      .subscribe((res: ResponseModel) => {
        this.uom = res.Data;
      });
  }

  setEditAccountId(id: any, subtotalId: any = '') {
    this.editAccountId = id;
    this.newAccountSubtotalId = subtotalId;
  }

  setEditSubtotalId(id: any) {
    this.editSubtotalId = id;
  }

  setDeleteSubtotalId(id: any) {
    this.deleteSubtotalId = id;
  }

  checkModalReturn(retVal: any) {
    // console.log('retVal: ' + retVal);
    if (retVal === 'acct-saved') {
      this.loadAccountHierarchy().then();
      // Refresh audit trail by cycling auditRecId so ngOnChanges fires on the child component
      const savedAuditRecId = this.auditRecId;
      this.auditRecId = '';
      setTimeout(() => {
        this.auditRecId = savedAuditRecId;
      }, 0);
    } else if (retVal === 'sub-saved') {
      this.loadAccountHierarchy().then();
    }
  }

  saveClose() {
    // currently still tied to button bar - just turned off button in config for now
  }

  saveNew() {
    // currently still tied to button bar - just turned off button in config for now
  }

  deleteItem() {
    let retMsg = '';
    const messages: string[] = [
      $localize`:Are you sure you want to delete account? @@areYouSureYouWantToDeleteAccount:Are you sure you want to delete account?`,
      this.account.number + ' : ' + this.account.name,
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.table
          .deleteDataById('account', this.accountID)
          .pipe(take(1))
          .subscribe(
            (result) => {
              retMsg = result;
              // success
            },
            (error: HttpErrorResponse) => {
              console.log(error);
            },
            () => {
              this.loadAccountHierarchy().then();
            }
          );
      },
      () => {
        this.notification.showSuccessToast($localize`:Delete Canceled @@deleteCanceled:Delete Canceled`);
      }
    );
  }

  deleteSubtotalItem() {
    let retMsg = '';
    const messages: string[] = [
      $localize`:Confirmation for Delete Account Subtotal @@confirmDeleteAccountSubtotal:Are you sure you want to delete account subtotal?`,
      this.deleteSubtotalName,
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.table
          .deleteDataById('account_subtotal', this.deleteSubtotalId)
          .pipe(take(1))
          .subscribe(
            (result) => {
              retMsg = result;
              // success
            },
            (error: HttpErrorResponse) => {
              // this.notification.error($localize`:Error @@error:Error`);
              console.log(error);
            },
            () => {
              // this.onClose();
              this.loadAccountHierarchy().then();
            }
          );
      },
      () => {
        this.notification.showSuccessToast($localize`:Delete Canceled @@deleteCanceled:Delete Canceled`);
      }
    );
  }

  resetForm() {
    this.accountForm.reset();
  }

  onClose() {
    // Prefer previous in-app URL (if any); else fall back to the list
    const fallback = '/admin/account-list';
    this.nav.backOr(fallback);
  }

  onSave() {
    // currently still tied to button bar - just turned off button in config for now
  }

  print(): void {
    if (this.accountPrintRoot) {
      window.print();
    }
  }

  sendReportEmail(): void {
    try {
      // Ensure the print section exists and capture its HTML
      const el: HTMLElement | undefined = this.accountPrintRoot?.nativeElement as HTMLElement;
      const html: string = el?.innerHTML ?? '';

      // Basic guard: require some HTML for attachment when user checks Attach Report
      if (!html || html.trim().length === 0) {
        // Still open modal so user can send message without attachment, but inform via toast
        this.notification.showWarningToast($localize`:Nothing to attach @@nothingToAttach:Nothing to attach for the email attachment`);
      }

      const dt = new Date();
      const ts = dt.toISOString().slice(0, 19).replace(/[:T]/g, '-');

      // Assemble object expected by <app-send-email-modal>
      this.reportDataToSendInEmail = {
        // Optional: prefill To. Leave empty so user can choose in modal, or set to a known address if desired
        toEmail: (this as any).defaultReportRecipient || '',
        Subject: $localize`:Account Hierarchy @@accountHierarchy:Account Hierarchy`,
        body: $localize`:Please see the attached account hierarchy. @@seeAttachedAccountHierarchy:Please see the attached account hierarchy.`,
        header_id: null,
        entity_id: null,
        activity_message: 'Account hierarchy emailed',
        // Attachment
        pdfHtml: html,
        FileName: `account_hierarchy_${ts}.pdf`,
        // Rendering hints used by send-email-modal service
        print_orientation: '2',
        convert_attachment_to: 'pdf',
      };
    } catch (e) {
      console.error(e);
      this.notification.showWarningToast($localize`:Unable to prepare email @@unableToPrepareEmail:Unable to prepare email`);
    }
  }

  exportToExcel(): void {
    try {
      const rows: any[] = [];
      const headers = [
        'Subtotal Name', 'Type', 'Sort', 'Subtotal Active', 'Is Parent', 'Child Count', 'Subtotal CF',
        'Account Name', 'Account Number', 'Debit', 'Account Active', 'Control', 'Balance', 'Used', 'Account CF'
      ];

      const toBoolMark = (v: any) => (v ? 'Y' : 'N');
      const toNumber = (v: any) => (v == null || v === '' ? '' : String(v));
      const toMoney = (v: any) => (v == null ? '' : String(v));

      const data = Array.isArray(this.hierarchicalAccounts) ? this.hierarchicalAccounts : [];
      for (const record of data) {
        const children = Array.isArray(record?.childAccounts) ? record.childAccounts : [];
        if (children.length === 0) {
          rows.push([
            record?.subtotal_name ?? '',
            record?.type_name ?? '',
            toNumber(record?.sort),
            toBoolMark(record?.active),
            toBoolMark(record?.is_parent),
            toNumber(record?.child_count ?? 0),
            record?.cf_section ?? '',
            '', '', '', '', '', '', '', ''
          ]);
        } else {
          for (const detail of children) {
            rows.push([
              record?.subtotal_name ?? '',
              record?.type_name ?? '',
              toNumber(record?.sort),
              toBoolMark(record?.active),
              toBoolMark(record?.is_parent),
              toNumber(record?.child_count ?? children.length),
              record?.cf_section ?? '',
              detail?.account_name ?? '',
              toNumber(detail?.number),
              toBoolMark(detail?.debit),
              toBoolMark(detail?.active),
              toBoolMark(detail?.control_account),
              toMoney(detail?.account_balance),
              toBoolMark(detail?.in_use),
              detail?.cf_section ?? ''
            ]);
          }
        }
      }

      // Build worksheet from array-of-arrays, prepend headers
      const aoa: any[] = [headers, ...rows];
      const ws: xlsx.WorkSheet = xlsx.utils.aoa_to_sheet(aoa);
      // Optional: set column widths for readability
      const colWidths = headers.map(h => ({wch: Math.max(12, String(h).length + 2)}));
      (ws as any)['!cols'] = colWidths;

      // Create workbook and write as .xlsx
      const wb: xlsx.WorkBook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(wb, ws, 'AccountHierarchy');

      const dt = new Date();
      const ts = dt.toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const fileName = `account_hierarchy_${ts}.xlsx`;
      xlsx.writeFile(wb, fileName, {bookType: 'xlsx', type: 'binary'} as any);

      this.notification.showSuccessToast($localize`:Export complete @@exportComplete:Export complete`);
    } catch (e) {
      console.error(e);
      this.notification.showWarningToast($localize`:Export failed @@exportFailed:Export failed`);
    }
  }
}
